/**
 * UserMedia Remapper
 * 
 * Maps raw API/Firestore data to UserMedia domain model.
 * Part of the Domain layer - handles data transformation.
 * 
 * Architecture:
 * Presentation → UseCase → Service (gets raw data) → Remapper (transforms) → UseCase (returns) → Presentation
 */

import type { JsonObject } from '@/data/types/json';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { UserMediaDto } from '@/data/dtos/userMedia/userMediaDto';
import type { TranscodeUserContentRequestDto } from '@/data/dtos/userMedia/transcodeUserContentRequestDto';
import type { PublishContentRequest } from '@/domain/entities/publish/publishContentRequest';
import type { UploadFileInfo } from '@/domain/entities/publish/uploadFileInfo';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';
import { ContentReplyCommentInfoRemapper } from '@/domain/remappers/comment/contentReplyCommentInfoRemapper';
import { generateUuidV1 } from '@/domain/utils/uuidUtils';

export class UserMediaRemapper {
  /**
   * Map raw API response data to UserMedia model
   * Handles field name differences and provides default values
   */
  static fromJson(data: JsonObject): UserMediaDto {
    const replyInfo = data['contentReplyCommentInfo'] as JsonObject | undefined;
    return {
      // Basic fields
      id: (data['id'] as string | undefined) || '',
      firebaseId: data['firebaseId'] as string | undefined,
      createdTimestamp: (data['created_timestamp'] as number | undefined) || (data['createdTimestamp'] as number | undefined) || 0,
      updatedTimestamp: (data['updatedTimestamp'] as number | undefined) || 0,
      isProcessing: (data['isProcessing'] as boolean | undefined) || false,

      // Media fields - handle backend field name differences
      type: (data['type'] as number | undefined) || 1,
      relativePath: (data['path'] as string | undefined) || (data['relativePath'] as string | undefined) || '',
      relativeThumbnailPath: (data['thumbnail'] as string | undefined) || (data['relativeThumbnailPath'] as string | undefined) || '',
      aspectRatio: (data['aspectRatio'] as number | undefined) || VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
      duration: data['duration'] as number | undefined,
      resolution: data['resolution'] as number | undefined,

      // User fields
      userName: data['userName'] as string | undefined,

      // Content fields
      description: data['description'] as string | undefined,
      title: data['title'] as string | undefined,
      hashTags: (data['hashTags'] as string[] | undefined) || [],
      contentTags: (data['contentTags'] as string[] | undefined) || [],
      contentLink: data['contentLink'] as string | undefined,
      mentions: data['mentions'] as string[] | undefined,

      // Engagement fields
      likes: data['likes'] as number | undefined,
      comments: data['comments'] as number | undefined,
      coins: data['coins'] as number | undefined,
      views: data['views'] as number | undefined,

      // Premium/monetization fields
      charge: data['charge'] as number | undefined,
      unlockCharge: data['unlockCharge'] as number | undefined,
      allowUnlock: data['allowUnlock'] !== false, // Default true
      typeLimit: data['typeLimit'] as number | undefined,

      // Settings/permissions
      isDraft: (data['isDraft'] as boolean | undefined) || false,
      allowComments: data['allowComments'] !== false, // Default true
      showCoins: data['showCoins'] !== false, // Default true
      allowEarlyAccess: (data['allowEarlyAccess'] as boolean | undefined) || false,

      // Social fields
      shareLink: (data['shareLink'] as string | undefined) || '',

      // Status fields
      reports: (data['reports'] as number | undefined) || 0,
      blocked: (data['blocked'] as boolean | undefined) || false,
      explicit: data['explicit'] as number | undefined,
      hasErrors: data['hasErrors'] as boolean | undefined,
      schedule: data['schedule'] as number | undefined,

      // Local/system flags
      isStartUp: (data['isStartUp'] as boolean | undefined) || false,
      isLocal: (data['isLocal'] as boolean | undefined) || false,
      // Flutter never persists this field (`@JsonKey(includeToJson: false, includeFromJson: false)`),
      // so Firestore docs never carry it — default to true. Flutter only sets it
      // to false locally for non-content surfaces (chat, feedback, community).
      isContent: (data['isContent'] as boolean | undefined) ?? true,

      // Additional fields
      previews: data['previews'] as string[] | undefined,

      contentReplyCommentInfo: replyInfo
        ? ContentReplyCommentInfoRemapper.fromJson(replyInfo)
        : undefined,
    };
  }

  /**
   * Map raw Firestore data to User Media Entity
   */
  static toEntity(data: any): UserMediaEntity {
    const dto = this.fromJson(data);
    return {
      ...dto
    } as UserMediaEntity
  };


  static fromJsonList(dataList: JsonObject[]): UserMediaDto[] {
    return dataList.map(data => UserMediaRemapper.fromJson(data));
  }

  /**
   * Build the payload for the `transcodeUserVideo` cloud function.
   * Matches Flutter's CloudFunctionsService.transcodeUserContent body verbatim
   * (lib/services/cloud_functions_service.dart:118).
   */
  static toTranscodeRequest(
    request: PublishContentRequest,
    uploadInfo: UploadFileInfo,
    dynamicUrl: string,
    uploadPath: string,
  ): TranscodeUserContentRequestDto {
    const { videoMeta, descriptionResult, contentSettings, monetizationResult } = request;
    const aspectRatio = videoMeta.width > 0 && videoMeta.height > 0
      ? videoMeta.width / videoMeta.height
      : VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO;

    return {
      id: request.mediaId,
      dynamicUrl,
      filePath: uploadPath,
      title: null,
      description: descriptionResult.description || null,
      duration: uploadInfo.durationMs,
      typeLimit: uploadInfo.typeLimit,
      hashTags: descriptionResult.hashTags ?? [],
      fileExtension: uploadInfo.fileExtension,
      aspectRatio,
      timeStartExpression: null,
      timeEndExpression: null,
      contentLink: contentSettings.contentLink ?? null,
      userName: request.userName,
      isDraft: contentSettings.isDraft,
      allowComments: contentSettings.allowComments,
      allowUnlock: monetizationResult.allowUnlock,
      unlockCharge: monetizationResult.unlockCharge,
      showCoins: contentSettings.showCoins,
      // Flutter sends `contentReplyCommentInfo?.toJson()`
      // (lib/services/cloud_functions_service.dart:138) — a serialized map when the
      // video is published as a comment reply, JSON `null` otherwise.
      contentReplyCommentInfo: request.contentReplyCommentInfo
        ? ContentReplyCommentInfoRemapper.toJson(
            ContentReplyCommentInfoRemapper.toDto(request.contentReplyCommentInfo),
          )
        : null,
      mentions: descriptionResult.mentions ?? [],
      schedule: contentSettings.schedule,
      shareLink: generateUuidV1(),
      allowEarlyAccess: contentSettings.allowEarlyAccess,
      boostOptionId: monetizationResult.boostOptionId,
    };
  }
}